"use client";

import { memo, useState, useCallback } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  useA2UIComponent,
  ComponentNode,
  type A2UIComponentProps,
  type Types,
} from "@a2ui/react";

export const BeeOSModal = memo(function BeeOSModal({
  node,
  surfaceId,
}: A2UIComponentProps<Types.ModalNode>) {
  useA2UIComponent(node, surfaceId);
  const props = node.properties;
  const [open, setOpen] = useState(false);

  const openModal = useCallback(() => setOpen(true), []);

  const hostStyle: React.CSSProperties =
    node.weight !== undefined
      ? ({ "--weight": node.weight } as React.CSSProperties)
      : {};

  return (
    <div className="a2ui-modal" style={hostStyle}>
      <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
        {/* 
         * Don't use Trigger asChild — the entryPointChild may itself be a
         * Button, causing nested-button and duplicate-click issues. Instead
         * use a plain wrapper div with onClick, matching Google's approach.
         */}
        <div
          role="button"
          tabIndex={0}
          className="cursor-pointer"
          onClick={openModal}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openModal();
            }
          }}
        >
          <ComponentNode
            node={props.entryPointChild}
            surfaceId={surfaceId}
          />
        </div>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border border-border/30 bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-2xl">
            <ComponentNode
              node={props.contentChild}
              surfaceId={surfaceId}
            />
            <DialogPrimitive.Close className="absolute right-4 top-4 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground/60 transition-colors hover:bg-muted/60 hover:text-foreground focus:outline-none">
              <svg
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.7816 4.03157C12.0062 3.80702 12.0062 3.44295 11.7816 3.2184C11.5571 2.99385 11.193 2.99385 10.9685 3.2184L7.50005 6.68682L4.03164 3.2184C3.80708 2.99385 3.44301 2.99385 3.21846 3.2184C2.99391 3.44295 2.99391 3.80702 3.21846 4.03157L6.68688 7.49999L3.21846 10.9684C2.99391 11.193 2.99391 11.557 3.21846 11.7816C3.44301 12.0061 3.80708 12.0061 4.03164 11.7816L7.50005 8.31316L10.9685 11.7816C11.193 12.0061 11.5571 12.0061 11.7816 11.7816C12.0062 11.557 12.0062 11.193 11.7816 10.9684L8.31322 7.49999L11.7816 4.03157Z"
                  fill="currentColor"
                  fillRule="evenodd"
                  clipRule="evenodd"
                />
              </svg>
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </div>
  );
});
